import React, { useState, useMemo } from 'react';
import { Search, Plus, X } from 'lucide-react';
import { sans, mono, green, greenLight, useTheme } from '../../tokens';

const inputStyle = (t) => ({
  fontFamily: sans,
  fontSize: 12,
  padding: '8px 12px',
  border: '1px solid ' + t.border,
  borderRadius: 6,
  background: t.inputBg,
  color: t.textPrimary,
  outline: 'none',
  width: '100%',
  boxSizing: 'border-box',
});

const labelStyle = (t) => ({
  fontSize: 11,
  fontWeight: 600,
  color: t.textSecondary,
  fontFamily: sans,
  marginBottom: 6,
  display: 'block',
});

const sectionTitleStyle = (t) => ({
  fontSize: 12,
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: 0.5,
  color: t.textMuted,
  fontFamily: sans,
  marginBottom: 16,
  paddingBottom: 8,
  borderBottom: '1px solid ' + t.border,
});

const toggleStyle = (enabled, t) => ({
  width: 36,
  height: 20,
  borderRadius: 10,
  background: enabled ? green : t.border,
  position: 'relative',
  cursor: 'pointer',
  transition: 'background 0.15s',
  flexShrink: 0,
});

const toggleKnob = (enabled) => ({
  width: 16,
  height: 16,
  borderRadius: '50%',
  background: '#fff',
  position: 'absolute',
  top: 2,
  left: enabled ? 18 : 2,
  transition: 'left 0.15s',
  boxShadow: '0 1px 2px rgba(0,0,0,0.15)',
});

const MONTHS = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December',
];

const INITIAL_PERIODS = MONTHS.map((m, i) => ({
  id: i + 1,
  month: m,
  year: 2026,
  status: i < 2 ? 'closed' : i === 2 ? 'open' : 'open',
}));

const INITIAL_ACCOUNTS = [
  { id: 1, glNumber: '1000', name: 'Cash - Operating', type: 'asset', active: true },
  { id: 2, glNumber: '1100', name: 'Accounts Receivable', type: 'asset', active: true },
  { id: 3, glNumber: '1200', name: 'Prepaid Expenses', type: 'asset', active: true },
  { id: 4, glNumber: '2000', name: 'Accounts Payable', type: 'liability', active: true },
  { id: 5, glNumber: '2100', name: 'Accrued Liabilities', type: 'liability', active: true },
  { id: 6, glNumber: '3000', name: 'Common Shares', type: 'equity', active: true },
  { id: 7, glNumber: '3100', name: 'Retained Earnings', type: 'equity', active: true },
  { id: 8, glNumber: '4000', name: 'Revenue - Product', type: 'revenue', active: true },
  { id: 9, glNumber: '4100', name: 'Revenue - Services', type: 'revenue', active: false },
  { id: 10, glNumber: '5000', name: 'Cost of Goods Sold', type: 'expense', active: true },
  { id: 11, glNumber: '6000', name: 'Salaries & Wages', type: 'expense', active: true },
  { id: 12, glNumber: '6100', name: 'Rent & Occupancy', type: 'expense', active: true },
];

const ACCOUNT_TYPES = ['asset', 'liability', 'equity', 'revenue', 'expense'];

export default function FinancialSettings() {
  const t = useTheme();
  const savedFinancial = JSON.parse(localStorage.getItem('finkloz_financial_settings') || 'null');
  const [periodStructure, setPeriodStructure] = useState(savedFinancial?.periodStructure || 'monthly');
  const [periods, setPeriods] = useState(savedFinancial?.periods || INITIAL_PERIODS);
  const [accounts, setAccounts] = useState(savedFinancial?.accounts || INITIAL_ACCOUNTS);
  const [accountSearch, setAccountSearch] = useState('');
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccount, setNewAccount] = useState({ glNumber: '', name: '', type: 'expense' });
  const [taxForm, setTaxForm] = useState(savedFinancial?.taxForm || {
    gstHst: '',
    qst: '',
    defaultGst: '5.00',
    defaultQst: '9.975',
    sredFederal: '35',
    sredQuebec: '30',
    c3i: false,
  });
  const [saved, setSaved] = useState(false);
  const [dirty, setDirty] = useState(false);

  const markDirty = () => setDirty(true);

  const handleSave = () => {
    localStorage.setItem('finkloz_financial_settings', JSON.stringify({
      periodStructure, periods, accounts, taxForm,
    }));
    setSaved(true);
    setDirty(false);
    setTimeout(() => setSaved(false), 3000);
  };

  const togglePeriodStatus = (id, newStatus) => {
    setPeriods(prev => prev.map(p => p.id === id ? { ...p, status: newStatus } : p));
    markDirty();
  };

  const filteredAccounts = useMemo(() => {
    if (!accountSearch) return accounts;
    const q = accountSearch.toLowerCase();
    return accounts.filter(a =>
      a.glNumber.includes(q) || a.name.toLowerCase().includes(q) || a.type.includes(q)
    );
  }, [accounts, accountSearch]);

  const handleAddAccount = () => {
    if (!newAccount.glNumber || !newAccount.name) return;
    setAccounts(prev => [...prev, { id: Date.now(), ...newAccount, active: true }]);
    setNewAccount({ glNumber: '', name: '', type: 'expense' });
    setShowAddAccount(false);
    markDirty();
  };

  const statusBadge = (status) => {
    const colors = {
      open: { bg: greenLight, color: green },
      closed: { bg: '#FFF3E0', color: '#BA7517' },
      locked: { bg: '#FCEBEB', color: '#C0392B' },
    };
    const c = colors[status] || colors.open;
    return {
      padding: '3px 8px',
      borderRadius: 4,
      fontSize: 10,
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: 0.3,
      background: c.bg,
      color: c.color,
    };
  };

  return (
    <div style={{ maxWidth: 800 }}>
      <h2 style={{ fontSize: 16, fontWeight: 700, color: t.textPrimary, fontFamily: sans, margin: '0 0 4px' }}>
        Financial Settings
      </h2>
      <p style={{ fontSize: 12, color: t.textMuted, fontFamily: sans, margin: '0 0 24px' }}>
        Period management, chart of accounts, and tax configuration.
      </p>

      {/* Period Management */}
      <div style={sectionTitleStyle(t)}>Period Management</div>

      <div style={{ marginBottom: 16 }}>
        <label style={labelStyle(t)}>Period Structure</label>
        <select
          value={periodStructure}
          onChange={e => { setPeriodStructure(e.target.value); markDirty(); }}
          style={{ ...inputStyle(t), maxWidth: 300 }}
        >
          <option value="monthly">Monthly (12 periods)</option>
          <option value="4-4-5">4-4-5 Weekly</option>
          <option value="13-period">13-Period</option>
        </select>
      </div>

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['Period', 'Year', 'Status', 'Actions'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: h === 'Actions' ? 'right' : 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {periods.map(p => (
              <tr key={p.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {p.month}
                </td>
                <td style={{ padding: '10px 14px', color: t.textSecondary, fontFamily: mono, fontSize: 11 }}>
                  {p.year}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={statusBadge(p.status)}>{p.status}</span>
                </td>
                <td style={{ padding: '10px 14px', textAlign: 'right' }}>
                  <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                    {p.status === 'open' && (
                      <button
                        onClick={() => togglePeriodStatus(p.id, 'closed')}
                        style={{
                          padding: '4px 10px', border: '1px solid ' + t.border, borderRadius: 4,
                          background: 'transparent', color: t.textSecondary, fontSize: 10,
                          fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                        }}
                      >
                        Close
                      </button>
                    )}
                    {p.status === 'closed' && (
                      <>
                        <button
                          onClick={() => togglePeriodStatus(p.id, 'open')}
                          style={{
                            padding: '4px 10px', border: '1px solid ' + t.border, borderRadius: 4,
                            background: 'transparent', color: t.textSecondary, fontSize: 10,
                            fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                          }}
                        >
                          Reopen
                        </button>
                        <button
                          onClick={() => togglePeriodStatus(p.id, 'locked')}
                          style={{
                            padding: '4px 10px', border: '1px solid #C0392B', borderRadius: 4,
                            background: 'transparent', color: '#C0392B', fontSize: 10,
                            fontWeight: 600, fontFamily: sans, cursor: 'pointer',
                          }}
                        >
                          Lock
                        </button>
                      </>
                    )}
                    {p.status === 'locked' && (
                      <span style={{ fontSize: 10, color: t.textFaint, fontFamily: sans }}>Locked</span>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Chart of Accounts */}
      <div style={sectionTitleStyle(t)}>Chart of Accounts</div>

      <div style={{ display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center' }}>
        <div style={{ position: 'relative', flex: 1, maxWidth: 320 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: t.textMuted }} />
          <input
            type="text"
            placeholder="Search accounts..."
            value={accountSearch}
            onChange={e => setAccountSearch(e.target.value)}
            style={{ ...inputStyle(t), paddingLeft: 32 }}
          />
        </div>
        <button
          onClick={() => setShowAddAccount(!showAddAccount)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6,
            padding: '8px 16px', border: 'none', borderRadius: 6,
            background: green, color: '#fff', fontSize: 12, fontWeight: 600,
            fontFamily: sans, cursor: 'pointer',
          }}
        >
          <Plus size={14} />
          Add Account
        </button>
      </div>

      {showAddAccount && (
        <div style={{
          background: t.bgSurface, border: '1px solid ' + t.border,
          borderRadius: 8, padding: 16, marginBottom: 16,
        }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: t.textPrimary, fontFamily: sans, marginBottom: 12 }}>
            Add New Account
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr 160px auto', gap: 12, alignItems: 'end' }}>
            <div>
              <label style={labelStyle(t)}>GL Number *</label>
              <input
                type="text"
                value={newAccount.glNumber}
                onChange={e => setNewAccount(p => ({ ...p, glNumber: e.target.value }))}
                style={inputStyle(t)}
                placeholder="7000"
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Account Name *</label>
              <input
                type="text"
                value={newAccount.name}
                onChange={e => setNewAccount(p => ({ ...p, name: e.target.value }))}
                style={inputStyle(t)}
                placeholder="Account name"
              />
            </div>
            <div>
              <label style={labelStyle(t)}>Type</label>
              <select
                value={newAccount.type}
                onChange={e => setNewAccount(p => ({ ...p, type: e.target.value }))}
                style={inputStyle(t)}
              >
                {ACCOUNT_TYPES.map(at => (
                  <option key={at} value={at}>{at.charAt(0).toUpperCase() + at.slice(1)}</option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleAddAccount} style={{
                padding: '8px 14px', border: 'none', borderRadius: 6,
                background: green, color: '#fff', fontSize: 12, fontWeight: 600,
                fontFamily: sans, cursor: 'pointer',
              }}>
                Add
              </button>
              <button onClick={() => setShowAddAccount(false)} style={{
                padding: '8px 10px', border: '1px solid ' + t.border, borderRadius: 6,
                background: 'transparent', color: t.textSecondary, fontSize: 12,
                fontFamily: sans, cursor: 'pointer',
              }}>
                <X size={14} />
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={{
        border: '1px solid ' + t.border,
        borderRadius: 8,
        overflow: 'hidden',
        marginBottom: 32,
      }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: sans, fontSize: 12 }}>
          <thead>
            <tr style={{ background: t.bgSurface }}>
              {['GL #', 'Account Name', 'Type', 'Status'].map(h => (
                <th key={h} style={{
                  padding: '10px 14px',
                  textAlign: 'left',
                  fontSize: 10,
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: 0.5,
                  color: t.textMuted,
                  borderBottom: '1px solid ' + t.border,
                }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredAccounts.map(a => (
              <tr key={a.id} style={{ borderBottom: '1px solid ' + t.borderLight }}>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontFamily: mono, fontWeight: 600, fontSize: 11 }}>
                  {a.glNumber}
                </td>
                <td style={{ padding: '10px 14px', color: t.textPrimary, fontWeight: 500 }}>
                  {a.name}
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 4, fontSize: 10, fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: 0.3,
                    background: t.pillBg, color: t.textSecondary,
                  }}>
                    {a.type}
                  </span>
                </td>
                <td style={{ padding: '10px 14px' }}>
                  <div
                    onClick={() => {
                      setAccounts(prev => prev.map(ac => ac.id === a.id ? { ...ac, active: !ac.active } : ac));
                      markDirty();
                    }}
                    style={toggleStyle(a.active, t)}
                  >
                    <div style={toggleKnob(a.active)} />
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Tax Settings */}
      <div style={sectionTitleStyle(t)}>Tax Settings</div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>GST/HST Number</label>
          <input
            type="text"
            value={taxForm.gstHst}
            onChange={e => { setTaxForm(p => ({ ...p, gstHst: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
            placeholder="123456789 RT0001"
          />
        </div>
        <div>
          <label style={labelStyle(t)}>QST Number</label>
          <input
            type="text"
            value={taxForm.qst}
            onChange={e => { setTaxForm(p => ({ ...p, qst: e.target.value })); markDirty(); }}
            style={inputStyle(t)}
            placeholder="1234567890 TQ0001"
          />
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Default GST Rate (%)</label>
          <input
            type="text"
            value={taxForm.defaultGst}
            onChange={e => { setTaxForm(p => ({ ...p, defaultGst: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 120 }}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Default QST Rate (%)</label>
          <input
            type="text"
            value={taxForm.defaultQst}
            onChange={e => { setTaxForm(p => ({ ...p, defaultQst: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 120 }}
          />
        </div>
      </div>

      <div style={{ fontSize: 12, fontWeight: 600, color: t.textSecondary, fontFamily: sans, marginBottom: 12 }}>
        SR&ED Rate Assumptions
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
        <div>
          <label style={labelStyle(t)}>Federal ITC Rate (%)</label>
          <input
            type="text"
            value={taxForm.sredFederal}
            onChange={e => { setTaxForm(p => ({ ...p, sredFederal: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 120 }}
          />
        </div>
        <div>
          <label style={labelStyle(t)}>Quebec ITC Rate (%)</label>
          <input
            type="text"
            value={taxForm.sredQuebec}
            onChange={e => { setTaxForm(p => ({ ...p, sredQuebec: e.target.value })); markDirty(); }}
            style={{ ...inputStyle(t), maxWidth: 120 }}
          />
        </div>
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32 }}>
        <div
          onClick={() => { setTaxForm(p => ({ ...p, c3i: !p.c3i })); markDirty(); }}
          style={toggleStyle(taxForm.c3i, t)}
        >
          <div style={toggleKnob(taxForm.c3i)} />
        </div>
        <span style={{ fontSize: 12, color: t.textPrimary, fontFamily: sans }}>C3i Tax Credit eligible</span>
      </div>

      {/* Save */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        <button
          onClick={handleSave}
          disabled={!dirty}
          style={{
            padding: '10px 24px',
            border: 'none',
            borderRadius: 6,
            background: dirty ? green : t.border,
            color: dirty ? '#fff' : t.textMuted,
            fontSize: 13,
            fontWeight: 600,
            fontFamily: sans,
            cursor: dirty ? 'pointer' : 'default',
            transition: 'all 0.15s',
          }}
        >
          Save
        </button>
        {saved && (
          <span style={{ fontSize: 12, color: green, fontFamily: sans, fontWeight: 500 }}>
            Settings saved
          </span>
        )}
      </div>
    </div>
  );
}
